import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { readListState } from "./listState";
import { readStored, writeStored } from "./storage";
import {
  scopeKey,
  type ToolId,
  type ToolListState,
  type WorkspaceScope,
} from "./types";
export function useToolListState(
  scope: WorkspaceScope | null,
  toolId: ToolId,
  statuses = ["all"],
) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const key = scope ? `lazysem_tool_state_v1:${scopeKey(scope)}:${toolId}` : "";
  const search = params.toString(),
    allowed = statuses.join(",");
  const state = useMemo(
    () =>
      readListState(
        new URLSearchParams(search),
        key ? readStored(key) : null,
        allowed.split(","),
      ),
    [search, key, allowed],
  );
  const setState = useCallback(
    (patch: Partial<ToolListState>) => {
      const next = {
        ...state,
        ...patch,
        page:
          patch.page ??
          (patch.q !== undefined ||
          patch.status !== undefined ||
          patch.view !== undefined
            ? 1
            : state.page),
      };
      if (key) writeStored(key, next);
      const nextParams = new URLSearchParams(search);
      Object.entries(next).forEach(([k, value]) =>
        nextParams.set(k, String(value)),
      );
      setParams(nextParams, { replace: true });
    },
    [state, key, search, setParams],
  );
  const rememberPosition = useCallback(
    (anchorId: string) => {
      if (key) writeStored(`${key}:anchor`, anchorId);
    },
    [key],
  );
  useEffect(() => {
    if (!key) return;
    const anchor = readStored(`${key}:anchor`);
    if (typeof anchor !== "string") return;
    const find = () => {
      const el = document.getElementById(`tool-row-${anchor}`);
      if (el) {
        el.scrollIntoView({ block: "nearest" });
        return true;
      }
      return false;
    };
    if (find()) return;
    const observer = new MutationObserver(() => {
      if (find()) observer.disconnect();
    });
    observer.observe(document.querySelector("main") || document.body, {
      childList: true,
      subtree: true,
    });
    const timeout = window.setTimeout(() => observer.disconnect(), 2000);
    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [key, location.key]);
  return {
    state,
    setState,
    clearFilters: () => setState({ q: "", status: "all", page: 1 }),
    rememberPosition,
  };
}
